import playerVolume from "./player_volume";

class PlayerController {
  constructor() {
    this.activeTrack = null;
    this.activeTrackIndex = null;
    this.isPlayerVisible = false;
    this.isPlaying = false;
    this.isReady = false;
    this.trackList = null;
    this.initializeYouTubePlayer();
  }

  initializeYouTubePlayer() {
    this.player = new YT.Player('youtube-player', {
      height: '100%',
      width: '100%',
      events: {
        'onReady': this.bind.bind(this),
        'onStateChange': this.onPlayerStateChange.bind(this)
      },
      playerVars: {rel: 0, controls: 1}
    });
  }

  bind() {
    this.isReady = true;
    this.$headerPlayButton = $('.eras-show .header .play-button');
    this.$section = $('#era_section');
    this.$playerPlayButton = $('.player .play-button');
    this.$playerPreviousButton = $('.player .previous-button');
    this.$playerNextButton = $('.player .next-button');
    this.$playerTrackTitle = $('.player .track-title');
    this.$headerPlayButton.on('click', this.onHeaderPlayButtonClick.bind(this));
    this.$playerPlayButton.on('click', this.onPlayerPlayButtonClick.bind(this));
    this.$playerPreviousButton.on('click', this.previous.bind(this));
    this.$playerNextButton.on('click', this.next.bind(this));
    this.$section.on('click', '[data-track]', this.onTrackClick.bind(this));
    this.$section.on('turbo:frame-load', this.onSectionChange.bind(this));
    window.addEventListener('player:volume', this.applyVolume.bind(this));
    this.applyVolume();
    this.emitState();
  }

  onHeaderPlayButtonClick() {
    if (this.isVisibleTrackListActive()) {
      this.togglePlayPause();
    } else {
      this.playVisibleTrackList(0);
    }
  }

  onTrackClick(event) {
    const $element = $(event.currentTarget);
    const index = parseInt($element.data('index'));

    if (!this.isVisibleTrackListActive()) {
      this.playVisibleTrackList(index);
      return;
    }

    if (index === this.activeTrackIndex) {
      this.togglePlayPause();
    } else {
      this.play(index);
    }
  }

  onPlayerPlayButtonClick() {
    if (this.trackList) {
      this.togglePlayPause();
    } else {
      this.playVisibleTrackList(0);
    }
  }

  // Within the first 3 seconds, go to the previous track; after that (or on
  // the first track), restart the current one.
  previous() {
    if (!this.canSkip()) return;

    const previousIndex = this.findPlayableIndex(this.activeTrackIndex, -1);
    if (previousIndex === null || this.player.getCurrentTime() > 3) {
      this.seek(0);
    } else {
      this.changeTrack(previousIndex);
    }
  }

  next() {
    if (!this.canSkip()) return;

    const nextIndex = this.findPlayableIndex(this.activeTrackIndex, 1);
    if (nextIndex === null) {
      this.stopAtFirstTrack();
    } else {
      this.changeTrack(nextIndex);
    }
  }

  // Switches track while preserving play state: keeps playing if playing,
  // otherwise cues the new track paused.
  changeTrack(index) {
    if (this.isPlaying) {
      this.play(index);
      return;
    }

    this.setActiveTrack(index);
    this.player.cueVideoById(this.activeTrack.youtube_video_id);
    this.emitState();
  }

  // Jumps to the given time, preserving play state.
  seek(seconds) {
    if (!this.canSkip()) return;

    const time = Math.max(0, Math.min(seconds, this.player.getDuration()));
    const state = this.player.getPlayerState();

    // seekTo on a cued video leaves the player stuck in the unstarted state;
    // re-cueing at the target time keeps it paused there instead.
    if (!this.isPlaying && (state === YT.PlayerState.CUED || state === YT.PlayerState.UNSTARTED)) {
      this.player.cueVideoById(this.activeTrack.youtube_video_id, time);
    } else {
      this.player.seekTo(time, true);
    }
    this.emitProgress(time);
  }

  applyVolume() {
    if (this.isDetached()) return;

    this.player.setVolume(playerVolume.level);
    if (playerVolume.muted) {
      this.player.mute();
    } else {
      this.player.unMute();
    }
    this.volumeAppliedAt = Date.now();
  }

  // Picks up changes made with YouTube's own controls. The player reports a
  // change only after a round trip to the iframe, so skip readings taken just
  // after applying ours, which would still show the old values.
  syncVolume() {
    if (Date.now() - this.volumeAppliedAt < 1000) return;

    playerVolume.update({ level: this.player.getVolume(), muted: this.player.isMuted() });
  }

  // Leaving an era removes its iframe, but nothing else stops this controller.
  // Its player keeps reporting stale cached time and volume, which would fight
  // the current era's player.
  isDetached() {
    return !document.contains(this.player.getIframe());
  }

  canSkip() {
    return this.isReady && Boolean(this.activeTrack);
  }

  // Returns the index of the nearest track after (step 1) or before (step -1)
  // fromIndex that has a video, or null if there is none.
  findPlayableIndex(fromIndex, step) {
    const tracks = this.trackList.tracks;
    for (let i = fromIndex + step; i >= 0 && i < tracks.length; i += step) {
      if (tracks[i].youtube_video_id) return i;
    }
    return null;
  }

  playVisibleTrackList(index) {
    this.trackList = window.visibleTrackList;
    this.play(index);
  }

  togglePlayPause() {
    if (!this.isReady) return;

    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  play(newTrackIndex) {
    const wasPlaying = this.isPlaying;
    if (newTrackIndex !== undefined) {
      if (!this.trackList.tracks[newTrackIndex].youtube_video_id) return;

      this.setActiveTrack(newTrackIndex);
      if (wasPlaying) this.changingTrack = true;
      this.isPlaying = true;
      this.player.loadVideoById(this.activeTrack.youtube_video_id);
    } else {
      this.isPlaying = true;
      this.player.playVideo();
    }
    this.$playerPlayButton.attr('data-playing', 'true');
    if (this.isVisibleTrackListActive()) {
      this.setTrackListPlaying(true);
    }
    this.emitState();
  }

  setTrackListPlaying(value) {
    const $activeRow = this.getActiveTrackRow();

    if (value) {
      this.ensurePlayerIsVisible();
      this.$headerPlayButton.attr('data-playing', 'true');
      $activeRow.addClass('playing');
    } else {
      this.$headerPlayButton.removeAttr('data-playing');
      $activeRow.removeClass('playing');
    }
  }

  pause() {
    this.processStop();
    this.player.pauseVideo();
  }

  processStop() {
    this.isPlaying = false;
    this.$playerPlayButton.removeAttr('data-playing');
    this.setTrackListPlaying(false);
    this.emitState();
  }

  onSectionChange() {
    // Only reset track list playing if we're on a different section than the
    // active track list.
    if (!this.isVisibleTrackListActive()) {
      this.setTrackListPlaying(false);
    }
  }

  onPlayerStateChange(event) {
    if (!this.isReady) return;

    // When calling loadVideoById to change the track, the player will fire
    // a pause state change event after our code completes. We want to ignore
    // that event, i.e., not treat it as a user invoked pause. The player will
    // also fire a play event, which will be ignored in the case statement.
    if (this.changingTrack && event.data === YT.PlayerState.PAUSED) {
      this.changingTrack = false;
      return;
    }

    switch(event.data) {
      case YT.PlayerState.PLAYING: // 1
        if (!this.isPlaying) {
          this.play();
        }
        break;
      case YT.PlayerState.PAUSED: // 2
        if (this.isPlaying) {
          this.pause();
        }
        break;
      case YT.PlayerState.ENDED: // 0
        this.processEnded();
        break;
      case YT.PlayerState.CUED: // 5
        // A newly cued video's duration isn't known until now, and we don't
        // poll while paused.
        this.emitProgress();
        break;
    }
  }

  processEnded() {
    const nextIndex = this.findPlayableIndex(this.activeTrackIndex, 1);
    if (nextIndex === null) {
      this.stopAtFirstTrack();
    } else {
      this.play(nextIndex);
    }
  }

  stopAtFirstTrack() {
    this.processStop();
    this.setActiveTrack(this.findPlayableIndex(-1, 1));
    this.player.cueVideoById(this.activeTrack.youtube_video_id);
    this.emitState();
  }

  setActiveTrack(index) {
    this.getActiveTrackRow().removeClass('playing active');
    this.activeTrack = this.trackList.tracks[index];
    this.activeTrackIndex = index;
    this.getActiveTrackRow().addClass('active');
  }

  isVisibleTrackListActive() {
    return this.trackList?.id === window.visibleTrackList.id;
  }

  ensurePlayerIsVisible() {
    if (this.isPlayerVisible === false) {
      $('#album-poster').hide();
      $('#youtube-player').show();
      this.isPlayerVisible = true;
    }
  }

  getActiveTrackRow() {
    if (!this.isVisibleTrackListActive()) return $();

    const index = this.activeTrackIndex;
    const $rows = this.$section.find(`[data-track][data-index=${index}]`);
    const $visibleRows = $rows.filter(':visible');

    return $visibleRows.length ? $visibleRows : $rows;
  }

  emitState() {
    const canSkip = this.canSkip();
    this.$playerPreviousButton.prop('disabled', !canSkip);
    this.$playerNextButton.prop('disabled', !canSkip);
    if (this.activeTrack) this.$playerTrackTitle.text(this.activeTrack.title);

    if (this.isPlaying) {
      this.startProgressUpdates();
    } else {
      this.stopProgressUpdates();
    }
    this.emitProgress();

    window.dispatchEvent(new CustomEvent("player:state", {
      detail: {
        track: this.activeTrack,
        isPlaying: this.isPlaying,
        isReady: this.isReady,
        canSkip
      }
    }));
  }

  // The IFrame API has no time-update event, so poll while playing.
  startProgressUpdates() {
    if (this.progressInterval) return;
    this.progressInterval = setInterval(() => {
      if (this.isDetached()) {
        this.stopProgressUpdates();
        return;
      }

      this.emitProgress();
      this.syncVolume();
    }, 250);
  }

  stopProgressUpdates() {
    clearInterval(this.progressInterval);
    this.progressInterval = null;
  }

  emitProgress(currentTime = this.player.getCurrentTime()) {
    // Right after a track switch YouTube still reports the previous video's
    // time and duration (or no video data at all, just after a cue), so
    // report zero until the new video has loaded.
    const loaded = Boolean(this.activeTrack) &&
      this.player.getVideoData()?.video_id === this.activeTrack.youtube_video_id;

    window.dispatchEvent(new CustomEvent("player:progress", {
      detail: {
        currentTime: loaded ? currentTime : 0,
        duration: loaded ? this.player.getDuration() : 0
      }
    }));
  }
}

window.PlayerController = PlayerController;
